<!-- Competition Management -->
<app-intro-section></app-intro-section>
@if ((connectionState$ | async) === 'checking') {
<div class="state-checking">
  <mat-progress-spinner
    mode="indeterminate"
    diameter="48"
  ></mat-progress-spinner>
</div>
} @if ((connectionState$ | async) === 'disconnected') {
<app-connect-notion-cta
  message="Competitions sync your habit data from Notion — connect your workspace to join or create one."
>
</app-connect-notion-cta>
} @if ((connectionState$ | async) === 'connected') {
<div class="container">
  <div>
    <mat-card>
      <mat-card-title style="text-align: center">
        <strong>Manage Competitions</strong>
      </mat-card-title>

      <!-- Every competition the user is currently part of, as compact cards in
           a grid. Backlog #234 (2), cap-raise rule (b): a user may hold at most
           one REGULAR (non-featured) competition plus the FEATURED one, so this
           grid can render up to two cards — which is why it is a compact card
           per competition rather than the ~600px stacked block it used to be.
           The featured competition renders here too (with its own chip and
           Share button) once joined, instead of a second time below. -->
      @if ((myCompetitions | async); as competitions) {
      @if (competitions.length) {
      <h2 class="section-title">Your competitions</h2>
      <div class="my-competitions">
        @for (competition of competitions; track competition.id) {
        <div
          class="active-comp comp-card"
          [class.comp-card-featured]="competition.featured"
        >
          <div class="comp-chips">
            <span
              class="status-badge"
              [ngClass]="{
                'status-active': competition.status === 'ACTIVE',
                'status-upcoming': competition.status === 'UPCOMING'
              }"
            >
              {{ competition.status }}
            </span>
            @if (competition.featured) {
            <span class="featured-chip">Featured</span>
            }
          </div>
          <h3 class="comp-name">{{ competition.name }}</h3>
          <p class="comp-dates">
            {{ competition.startDate | date : "MMM d" }} –
            {{ competition.endDate | date : "MMM d, y" }}
          </p>
          @if (competition.participantCount != null) {
          <p class="comp-participants">{{ competition.participantCount }} {{ competition.participantCount === 1 ? 'participant' : 'participants' }}</p>
          }
          @if (competition.description) {
          <p class="comp-description">{{ competition.description }}</p>
          } @if (competition.status === 'UPCOMING') {
          <p class="comp-upcoming-note">
            Points start counting on the start date.
          </p>
          } @if (competition.featured) {
          <p class="season-rules-line">
            @if (isSeasonOne(competition.name)) {
            Finishers earn a badge. The top scorer names the next season.
            <a routerLink="/season-1">Season 1 rules</a>
            } @else if (isNumberedSeason(competition.name)) {
            Finishers earn a badge. The top scorer names the next season.
            <a routerLink="/season-0">Season 0 rules</a> ·
            <a routerLink="/season-1">Season 1: January 2027</a>
            } @else {
            A warm-up for the new year. No badge, just the leaderboard.
            <a routerLink="/season-1">Season 1: January 2027</a>
            }
          </p>
          }
          <div class="comp-actions">
            @if (competition.inviteToken) {
            <button
              mat-stroked-button
              color="primary"
              class="invite-toggle"
              (click)="toggleInvite(competition.id)"
            >
              {{ isInviteOpen(competition.id) ? "Hide invite" : "Invite friends" }}
            </button>
            } @if (competition.featured && featuredCompetition &&
            featuredCompetition.id === competition.id) {
            <button
              mat-stroked-button
              color="primary"
              (click)="shareFeaturedCompetition()"
            >
              Share
            </button>
            }
            <button
              mat-button
              class="leave-button"
              (click)="leaveCompetition(competition.id)"
              [disabled]="isLeavingCompetition(competition.id)"
            >
              Leave
            </button>
          </div>
          @if (competition.inviteToken && isInviteOpen(competition.id)) {
          <div class="invite-panel">
            <p>Share this link:</p>
            <div class="invite-link-row">
              <input
                class="invite-input"
                type="text"
                [value]="buildInviteLink(competition.inviteToken)"
                readonly
              />
              <button
                class="copy-link-button"
                (click)="
                  copyInviteLink(buildInviteLink(competition.inviteToken))
                "
              >
                Copy Link
              </button>
            </div>
            <p class="invite-email-info">
              …or email the invite directly — they don't need an account yet.
            </p>
            <div class="invite-email-row">
              <input
                class="invite-input"
                type="email"
                placeholder="friend@example.com"
                [formControl]="inviteState(competition.id).control"
                (keyup.enter)="sendInviteEmail(competition.id)"
              />
              <button
                mat-stroked-button
                color="primary"
                (click)="sendInviteEmail(competition.id)"
                [disabled]="
                  inviteState(competition.id).sending ||
                  inviteState(competition.id).control.invalid
                "
              >
                @if (!inviteState(competition.id).sending) { Send invite } @if
                (inviteState(competition.id).sending) {
                <mat-progress-spinner
                  diameter="20"
                  mode="indeterminate"
                ></mat-progress-spinner>
                }
              </button>
            </div>
            @if (inviteState(competition.id).sentTo &&
            !inviteState(competition.id).error) {
            <p class="invite-email-success">
              Invite sent to {{ inviteState(competition.id).sentTo }}.
            </p>
            } @if (inviteState(competition.id).error) {
            <p class="invite-email-error">
              {{ inviteState(competition.id).error }}
            </p>
            }
          </div>
          }
        </div>
        }
      </div>
      } }

      <!-- Featured Competition promo: its own full-width row above the
           Create/Join pair. It used to live inside the Join column, which
           forced that column to ~640px against Create's 292px and pushed the
           Join toggle roughly 650px below the Create toggle, so the two
           buttons no longer read as a pair. It is a promo for one competition
           rather than part of the join form, so it gets its own row. It is
           hidden once the user has joined it — from then on the competition
           is represented by its card in the grid above, so it is never
           rendered twice. -->
      @if (featuredCompetition && !joinedIds.has(featuredCompetition.id)) {
      <div class="featured-section">
        <h2 class="section-title">Featured Competition</h2>
        <div class="competition-item featured-competition-item">
          <span class="featured-badge">Featured</span>
          <h3>
            <strong>{{ featuredCompetition.name }}</strong>
          </h3>
          <span
            class="status-badge"
            [ngClass]="{
              'status-active':
                featuredCompetition.status === CompetitionStatus.ACTIVE,
              'status-upcoming':
                featuredCompetition.status === CompetitionStatus.UPCOMING
            }"
            >{{ featuredCompetition.status }}</span
          >
          <p>{{ featuredCompetition.description }}</p>
          <p class="comp-dates">
            {{ featuredCompetition.startDate | date : "MMM d" }} –
            {{ featuredCompetition.endDate | date : "MMM d, y" }}
          </p>
          <p class="season-rules-line">
            @if (isSeasonOne(featuredCompetition.name)) {
            Finishers earn a badge. The top scorer names the next season.
            <a routerLink="/season-1">Season 1 rules</a>
            } @else if (isNumberedSeason(featuredCompetition.name)) {
            Finishers earn a badge. The top scorer names the next season.
            <a routerLink="/season-0">Season 0 rules</a> ·
            <a routerLink="/season-1">Season 1: January 2027</a>
            } @else {
            A warm-up for the new year. No badge, just the leaderboard.
            <a routerLink="/season-1">Season 1: January 2027</a>
            }
          </p>
          <div class="featured-actions">
            @if (featuredCompetition.status !== CompetitionStatus.COMPLETED) {
            <button
              mat-raised-button
              color="primary"
              (click)="joinCompetition(featuredCompetition)"
              [disabled]="isJoiningCompetition"
            >
              Join Competition
            </button>
            }
            <button
              mat-stroked-button
              color="primary"
              (click)="shareFeaturedCompetition()"
            >
              Share
            </button>
          </div>
        </div>
      </div>
      }

      <!-- Final podium of the caller's latest completed competition
           (GET /points/competition/podium). Shown under the featured promo,
           or alone when no featured competition is returned. -->
      @if (podium && podiumCategoryRows.length) {
      <div class="podium-section" data-testid="podium-section">
        <h2 class="section-title">Final podium</h2>
        <div class="competition-item podium-item">
          <h3>
            <strong>{{ podium.competitionName }}</strong>
          </h3>
          <p class="comp-dates">
            {{ podium.startDate | date : "MMM d" }} –
            {{ podium.endDate | date : "MMM d, y" }} ·
            {{ podium.totalParticipants }}
            {{ podium.totalParticipants === 1 ? "participant" : "participants" }}
          </p>
          <div class="podium-categories">
            @for (category of podiumCategoryRows; track category.key) {
            <div
              class="podium-category"
              [attr.data-testid]="'podium-category-' + category.key"
            >
              <h4>{{ category.label }}</h4>
              <ol>
                @for (row of category.rows; track row.place) {
                <li
                  class="podium-row place-{{ row.place }}"
                  [class.is-you]="row.isYou"
                  [attr.data-testid]="'podium-row-' + category.key + '-' + row.place"
                >
                  <span class="place">{{ row.label }}</span>
                  <span class="name"
                    >{{ row.name }}
                    @if (row.isYou) {
                    <span class="you">(you)</span>
                    }</span
                  >
                  <span class="value">{{ row.value }}</span>
                </li>
                }
              </ol>
            </div>
            }
          </div>
          @if (podiumOwnLine) {
          <p class="podium-own-line">{{ podiumOwnLine }}</p>
          }
          @if (podiumIsSeasonZero) {
          <p class="season-rules-line">
            The Season 0 hall of fame post follows on the blog.
          </p>
          }
        </div>
      </div>
      }

      <!-- Create Competition-->
      <!-- Flex Container for Buttons and Forms -->
      <div class="button-form-row">
        <!-- Create Competition Section -->
        <div class="button-form-item">
          @if (!hasRegularCompetition) {
          <button
            mat-button
            (click)="toggleCompetitionForm()"
            class="styled-button center-content"
          >
            {{ showCompetitionForm ? "Hide Form" : "CREATE NEW COMPETITION" }}
          </button>
          } @if (!hasRegularCompetition && showCompetitionForm)
          {
          <div class="center-content">
            <mat-card-subtitle>Create Competition</mat-card-subtitle>
            <mat-card-content class="card-content-centered">
              <form
                [formGroup]="competitionForm"
                (ngSubmit)="onSubmit()"
                class="form-column"
              >
                <mat-form-field>
                  <input
                    matInput
                    placeholder="Name"
                    formControlName="name"
                    required
                    maxlength="50"
                  />
                  <mat-hint align="end"
                    >{{
                      competitionForm.get("name")!.value?.length || 0
                    }}/50</mat-hint
                  >
                </mat-form-field>
                <mat-form-field>
                  <textarea
                    matInput
                    placeholder="Description"
                    formControlName="description"
                    maxlength="254"
                  ></textarea>
                  <mat-hint align="end"
                    >{{
                      competitionForm.get("description")!.value?.length || 0
                    }}/300</mat-hint
                  >
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Start Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="picker1"
                    placeholder="Start Date"
                    formControlName="startDate"
                  />
                  <mat-datepicker-toggle
                    matIconSuffix
                    [for]="picker1"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #picker1 startView="month"></mat-datepicker>
                  @if
                  (competitionForm.get('startDate')!.hasError('startDateNotBeforeToday'))
                  {
                  <mat-error> Start date cannot be before today. </mat-error>
                  }
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>End Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="picker2"
                    placeholder="End Date"
                    formControlName="endDate"
                  />
                  <mat-datepicker-toggle
                    matIconSuffix
                    [for]="picker2"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #picker2 startView="month"></mat-datepicker>
                  @if (competitionForm.get('endDate')!.errors?.dateLessThan) {
                  <mat-error> End date must be after start date. </mat-error>
                  }
                </mat-form-field>
                <mat-form-field subscriptSizing="dynamic">
                  <input
                    matInput
                    placeholder="Password"
                    formControlName="password"
                    type="password"
                    maxlength="50"
                  />
                  @if (!competitionForm.get('password')!.value) {
                  <mat-hint align="start" class="pw-hint">
                    Note: If no password is set, the competition will be public.
                  </mat-hint>
                  }
                  <mat-hint class="pw-count-hint" align="end"
                    >{{
                      competitionForm.get("password")!.value?.length || 0
                    }}/50</mat-hint
                  >
                </mat-form-field>
                <mat-button-toggle
                  mat-raised-button
                  color="primary"
                  type="submit"
                  (click)="onSubmit()"
                  [disabled]="
                    competitionForm.invalid || isSubmittingCompetition
                  "
                  style="margin-top: 30px"
                >
                  @if (!isSubmittingCompetition) {
                  <ng-container style="margin-top: 30px !important"
                    >Create Competition</ng-container
                  >
                  } @if (isSubmittingCompetition) {
                  <mat-progress-spinner
                    [diameter]="20"
                    mode="indeterminate"
                    color="accent"
                  ></mat-progress-spinner>
                  }
                </mat-button-toggle>
              </form>
            </mat-card-content>
          </div>
          }
        </div>
        <!-- Join Competition Section -->
        <div class="button-form-item2">
          @if (!hasRegularCompetition) {
          <button
            mat-button
            (click)="toggleCompetitionSearch()"
            class="styled-button center content"
          >
            {{ showCompetitionSearch ? "Hide Form" : "JOIN COMPETITION" }}
          </button>
          } @if (!hasRegularCompetition && showCompetitionSearch)
          {
          <div class="center-content">
            <mat-card-content class="card-content-centered">
              <form [formGroup]="searchForm" class="form-column">
                <mat-form-field appearance="outline">
                  <mat-label>Competition Name</mat-label>
                  <input
                    matInput
                    placeholder="Competition Name"
                    [formControl]="searchControl"
                    type="text"
                  />
                </mat-form-field>
              </form>
            </mat-card-content>
          </div>
          }
          <mat-card-content>
            @if (searchedCompetitions?.length > 0 && showCompetitionSearch &&
            !hasRegularCompetition) {
            <div class="search-results-container">
              <h2>Search Results</h2>
              <mat-list>
                @for (competition of searchedCompetitions; track competition) {
                @if (!featuredCompetition || competition.id !==
                featuredCompetition.id) {
                <mat-list-item>
                  <div class="competition-item">
                    <h3 mat-line>
                      <strong>{{ competition.name }}</strong>
                    </h3>
                    <h3 mat-line>*{{ competition.status }}*</h3>
                    <p mat-line>{{ competition.description }}</p>
                    <p mat-line>End Date: {{ competition.endDate | date }}</p>
                    <p mat-line>
                      Start Date: {{ competition.startDate | date }}
                    </p>
                    <button
                      mat-raised-button
                      color="primary"
                      style="margin-bottom: 20px"
                      (click)="joinCompetition(competition)"
                      [disabled]="
                        competition.status == CompetitionStatus.COMPLETED ||
                        isJoiningCompetition
                      "
                    >
                      Join Competition
                    </button>
                    @if (competition.isPrivate) {
                    <p mat-line>private</p>
                    } @if (!competition.isPrivate) {
                    <p mat-line>public</p>
                    }
                  </div>
                </mat-list-item>
                } }
              </mat-list>
            </div>
            }
          </mat-card-content>
        </div>
      </div>
    </mat-card>
  </div>
</div>
}
